import type { WatchStopHandle } from 'vue'
import { TimeOut } from '~/services/apis/timeout'

export const useApiLoader = <T = RecordSchema>(
	fn: () => Promise<T>,
	options: {
		loadOnMount?: boolean

		/**
		 * @description Return the watch hook from vue on what props you want to watch to update
		 * @example watcher: (arg: boolean) => watch(() => props.show, cb)
		 * @prop cb is the callback function that it listens to before trigger
		 * */
		watcher?: (cb: unknown) => WatchStopHandle
		lazyLoad?: boolean
	} = { loadOnMount: true, lazyLoad: false }
) => {
	const isLoading = ref(true)
	const data = ref<T>()

	const makeRequest = async () => {
		isLoading.value = true
		try {
			data.value = await fn()
		} finally {
			isLoading.value = false
		}
	}

	onMounted(() => {
		if (options?.loadOnMount !== false) {
			TimeOut(options?.lazyLoad ? 2000 : 0).then(() => {
				makeRequest()
			})
		}
	})

	if (options?.watcher) {
		options.watcher((e: boolean) => {
			if (e) {
				makeRequest()
			}
		})
	}

	return {
		isLoading: isLoading as Readonly<Ref<boolean>>,
		data: data as Ref<T>,
		refresh: makeRequest
	}
}
